import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Plus, Eye, Edit, Trash2, UserCheck, FolderOpen, MapPin, AlertCircle } from 'lucide-react';
import { enqueteursData } from '../../data/mockData';

const EnqueteursListing = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [formeJuridiqueFilter, setFormeJuridiqueFilter] = useState('');
  const [compteActifFilter, setCompteActifFilter] = useState('');
  const [sortField, setSortField] = useState('nom');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);

  // Filtrage et tri des enquêteurs
  const filteredAndSortedEnqueteurs = useMemo(() => {
    let filtered = enqueteursData.filter(enqueteur => {
      const fullName = `${enqueteur.prenom} ${enqueteur.nom}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                           enqueteur.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           enqueteur.ville.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           enqueteur.numeroCertification.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFormeJuridique = !formeJuridiqueFilter || enqueteur.formeJuridique === formeJuridiqueFilter;
      const matchesCompteActif = !compteActifFilter || 
                                  (compteActifFilter === 'actif' && enqueteur.compteActif) ||
                                  (compteActifFilter === 'inactif' && !enqueteur.compteActif);
      
      return matchesSearch && matchesFormeJuridique && matchesCompteActif;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, formeJuridiqueFilter, compteActifFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const uniqueFormesJuridiques = [...new Set(enqueteursData.map(enq => enq.formeJuridique))];

  // Statistiques rapides
  const stats = {
    total: enqueteursData.length,
    actifs: enqueteursData.filter(e => e.compteActif).length,
    inactifs: enqueteursData.filter(e => !e.compteActif).length,
    totalDossiers: enqueteursData.reduce((acc, e) => acc + e.dossiersEnCours, 0)
  };

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-port-gray-900">Gestion des Enquêteurs</h1>
          <p className="text-port-gray-600 mt-1">
            {filteredAndSortedEnqueteurs.length} enquêteur{filteredAndSortedEnqueteurs.length > 1 ? 's' : ''} trouvé{filteredAndSortedEnqueteurs.length > 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/admin/enqueteurs/nouveau" className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Enquêteur
        </Link>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-port-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email, ville, certification..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            {/* Bouton filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
              {(formeJuridiqueFilter || compteActifFilter) && (
                <span className="ml-2 bg-atl-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {(formeJuridiqueFilter ? 1 : 0) + (compteActifFilter ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filtres étendus */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-port-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Forme Juridique</label>
                  <select
                    value={formeJuridiqueFilter}
                    onChange={(e) => setFormeJuridiqueFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Toutes les formes</option>
                    {uniqueFormesJuridiques.map(forme => (
                      <option key={forme} value={forme}>{forme}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="form-label">Statut du compte</label>
                  <select
                    value={compteActifFilter}
                    onChange={(e) => setCompteActifFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Tous les comptes</option>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFormeJuridiqueFilter('');
                      setCompteActifFilter('');
                    }}
                    className="btn btn-secondary w-full"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tableau des enquêteurs */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Actions</th>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('nom')}
                >
                  <div className="flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Nom {getSortIcon('nom')}
                  </div>
                </th>
                <th className="mobile-hidden">Contact</th>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('ville')}
                >
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Ville {getSortIcon('ville')}
                  </div>
                </th>
                <th className="mobile-hidden">Forme Juridique</th>
                <th className="mobile-hidden">Certification</th>
                <th 
                  className="cursor-pointer hover:bg-port-gray-100"
                  onClick={() => handleSort('nombreDossiers')}
                >
                  <div className="flex items-center">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Dossiers {getSortIcon('nombreDossiers')}
                  </div>
                </th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedEnqueteurs.map(enqueteur => (
                <tr key={enqueteur.id} className="hover:bg-port-gray-50">
                  <td>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/admin/enqueteurs/${enqueteur.id}`}
                        className="p-2 text-atl-blue-600 hover:bg-atl-blue-100 rounded-lg transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <button
                        className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="font-medium text-port-gray-900">
                        {enqueteur.prenom} {enqueteur.nom}
                      </div>
                      <div className="text-sm text-port-gray-500">
                        {enqueteur.adresse}
                      </div>
                    </div>
                  </td>
                  <td className="mobile-hidden">
                    <div className="text-sm">
                      <div className="text-port-gray-900">{enqueteur.email}</div>
                      <div className="text-port-gray-500">{enqueteur.telephone}</div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {enqueteur.ville}
                    </span>
                  </td>
                  <td className="mobile-hidden">
                    <span className="text-sm text-port-gray-900">
                      {enqueteur.formeJuridique}
                    </span>
                  </td>
                  <td className="mobile-hidden">
                    <span className="text-sm text-port-gray-600">
                      {enqueteur.numeroCertification}
                    </span>
                  </td>
                  <td>
                    <div className="text-sm">
                      <div className="font-medium text-port-gray-900">
                        {enqueteur.nombreDossiers}
                      </div>
                      <div className="text-port-gray-500">
                        {enqueteur.dossiersEnCours} en cours
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      enqueteur.compteActif ? 'badge-success' : 'badge-secondary'
                    }`}>
                      {enqueteur.compteActif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedEnqueteurs.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-port-gray-900 mb-2">
              Aucun enquêteur trouvé
            </h3>
            <p className="text-port-gray-500">
              Essayez de modifier vos critères de recherche ou ajoutez un nouvel enquêteur.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnqueteursListing;

